<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Title -->
                    <span class="flex-grow-1" v-if="baseMenuItem"><b>{{ $t('menuItemIntro') }}</b> {{baseMenuItem.labels[currentLanguage.key]}}</span>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <div class="d-flex flex-column">
                    <!-- URL -->
                    <div role="group" class="form-group col-12 p-0">
                        <label class="">URL</label>
                        <CInput type="text" v-model="menuItem.url" placeholder="/pagina" :invalid-feedback="errorOutput['Url']" :is-valid="genericValidator(menuItem.url) && errorValidator('Url')"></CInput>
                        <!-- URL Preview -->
                        <div v-if="menuItem.url" class="mt-1">
                            <small class="text-muted">{{ $t('urlPreview') }} </small>
                            <a :href="fullUrl" target="_blank" rel="noopener noreferrer" class="url-preview-link">{{ fullUrl }}</a>
                        </div>
                        <!-- Static page selector -->
                        <div class="mt-2">
                            <small class="text-muted d-block mb-1">{{ $t('staticPageSelect') }}</small>
                            <select class="form-control form-control-sm" v-model="selectedStaticPage" @change="onStaticPageSelected">
                                <option value="">{{ $t('selectPage') }}</option>
                                <option v-for="page in staticPages" :key="page.id" :value="page.slug">{{ page.title }} (/{{ page.slug }})</option>
                            </select>
                        </div>
                        <div class="mt-1">
                            <small class="text-muted">{{ $t('menuUrlHint') }} <router-link to="/paginas/lista">{{ $t('pages') }}</router-link>.</small>
                        </div>
                    </div>
                    <!-- Labels -->
                    <div role="group" class="form-group col-12 p-0">
                        <label class=""> {{ $t('labelMandatory') }}</label>
                        <CInput type="text" v-model="menuItem.labels[currentLanguage.key]" :invalid-feedback="errorOutput['Labels']" :is-valid="genericValidator(menuItem.labels) && errorValidator('Labels')"></CInput>
                    </div>
                    <!-- Position -->
                    <div role="group" class="form-group col-12 p-0">
                        <label class=""> {{ $t('position') }}</label>
                        <CInput type="number" v-model.number="menuItem.position" :invalid-feedback="errorOutput['Position']" :is-valid="errorValidator('Position')"></CInput>
                        <!-- Menu Preview -->
                        <div class="menu-preview mt-2">
                            <small class="text-muted d-block mb-2"><b>{{ $t('menuPreview') }}</b></small>
                            <ol class="menu-preview-list">
                                <li v-for="(item, index) in previewMenu" :key="index" :class="{ 'menu-preview-current': item.isCurrent }">
                                    <span class="menu-preview-position">{{ item.position }}</span>
                                    <span class="menu-preview-label">{{ item.label }}</span>
                                    <span v-if="item.isCurrent" class="badge badge-primary ml-2">{{ $t('thisItem') }}</span>
                                </li>
                                <li v-if="!previewMenu.length" class="text-muted">{{ $t('noMenuItems') }}</li>
                            </ol>
                        </div>
                    </div>
                    <!-- IsVisible -->
                    <div role="group" class="form-group col-12 p-0">
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" v-model="menuItem.isVisible" id="isVisibleCheck">
                            <label class="form-check-label" for="isVisibleCheck">{{ $t('visible') }}</label>
                        </div>
                    </div>
                </div>
                <!-- Action button + submitting -->
                <div role="group" class="form-group col-12 d-flex justify-content-end">
                    <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                    <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                </div>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>

<script>
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import { mapState, mapActions } from 'vuex'
import LanguageDropdown from "@/controls/LanguageDropdown.vue";
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import FormLoading from '@/controls/FormLoading.vue'
import InputGetList from "@/classes/InputGetList";
import { STATICINFOS_SERVICE } from "@/services/staticInfos.service.js";
import { MENUITEMS_SERVICE } from "@/services/menuItems.service.js";

  export default {
  components: { LanguageDropdown, ConfirmationPopUp, FormLoading },
    props:{
        baseMenuItem:
        {
            type: Object,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            triedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            menuItem: { url: '', labels: {}, position: 0, isVisible: true },
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
            selectedStaticPage: "",
            staticPages: [],
            existingMenuItems: [],
        }
    },
    mounted: function()
    {
        this.resetMenuItem();
        this.loadStaticPages();
        this.loadMenuItems();
    },
    computed:
    {
        ...mapState({
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        fullUrl: function()
        {
            var baseUrl = process.env.VUE_APP_WEBSITE_URL || 'https://www.cfnumao.com';
            var path = this.menuItem.url || '';
            if (path && !path.startsWith('/')) { path = '/' + path; }
            return baseUrl + path;
        },
        previewMenu: function()
        {
            var currentId = this.baseMenuItem ? this.baseMenuItem.id : null;
            var currentLabel = this.menuItem.labels[this.currentLanguage.key];
            var currentPosition = Number(this.menuItem.position) || 0;

            // Filter out the item being edited (if any) so we can re-insert it at the new position
            var items = this.existingMenuItems.filter(function(item) {
                return currentId === null || item.id !== currentId;
            }).map(function(item) {
                return { id: item.id, label: item.label, position: item.position, isCurrent: false };
            });

            // Only show the current item in preview if it has a label
            if (currentLabel) {
                items.push({ id: currentId, label: currentLabel, position: currentPosition, isCurrent: true });
            }

            // Sort by position (ascending)
            return items.sort(function(a, b) { return a.position - b.position; });
        },
        errorOutput: function()
        {
            var result = [];

            if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
            {
                result = this.alertOutput.reduce(function(accum, currentVal) {
                accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate multilanguage fields
            for (const [key, value] of Object.entries(this.menuItem.labels)) { if(!value) { result['Labels'] = this.$i18n.t("validateBothTranslations"); break; }; }

            return result;
        },
        ...mapState('menuItems', ['putMenuItemOutput']),
        ...mapState('menuItems', ['postMenuItemOutput']),
    },
    methods:{
        resetMenuItem(){
            if(!this.baseMenuItem)
            {
                var labels = {};
                CONSTANTS.supportedLanguages.forEach(language => { labels[language.key] = ""; });

                this.menuItem = { url: '', labels: labels, position: 0, isVisible: true };
            }
            else
            {
                this.menuItem = JSON.parse(JSON.stringify(this.baseMenuItem));
            }
        },
        submitClicked(){
            this.triedSubmit = true;
            // Validate required fields
            var valid = true;
            if(!this.menuItem.url) { valid = false; }
            for (const [key, value] of Object.entries(this.menuItem.labels)) { if(!value) { valid = false; break; } }

            // Emit event
            if(valid)
            {
                this.submitting = true;
                this.$emit('submit-requested', this.menuItem);
            }
        },
        loadMenuItems()
        {
            var input = new InputGetList(100, 1, '', 1, 'Position');
            MENUITEMS_SERVICE.getMenuItems(input).then((result) => {
                if (result && result.data) {
                    this.existingMenuItems = result.data;
                }
            });
        },
        syncSelectedStaticPage()
        {
            if (!this.menuItem.url) {
                this.selectedStaticPage = "";
                return;
            }
            var slug = this.menuItem.url.replace(/^\//, '');
            var match = this.staticPages.find(function(p) { return p.slug === slug; });
            this.selectedStaticPage = match ? match.slug : "";
        },
        loadStaticPages()
        {
            var activeInput = new InputGetList(100, 1, '', 1, 'Id');
            activeInput.status = 'Active';
            var draftInput = new InputGetList(100, 1, '', 1, 'Id');
            draftInput.status = 'Draft';

            Promise.all([
                STATICINFOS_SERVICE.getStaticInfos(activeInput),
                STATICINFOS_SERVICE.getStaticInfos(draftInput)
            ]).then(([activeResult, draftResult]) => {
                var active = activeResult && activeResult.data && activeResult.data.staticInfos ? activeResult.data.staticInfos : [];
                var draft = draftResult && draftResult.data && draftResult.data.staticInfos ? draftResult.data.staticInfos : [];
                this.staticPages = active.concat(draft).filter(function(p) { return p.slug; });
                this.syncSelectedStaticPage();
            });
        },
        onStaticPageSelected()
        {
            if (this.selectedStaticPage) {
                this.menuItem.url = '/' + this.selectedStaticPage;
            }
        },
        genericValidator,
        errorValidator,
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;
            this.$router.push({name: "MenuItemsList" });
        },
    },
    watch:
    {
        putMenuItemOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        postMenuItemOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        baseMenuItem: function()
        {
            this.resetMenuItem();
        },
        alertType: function()
        {
            this.submitting = false;
        },
        alertOutput: function()
        {
            this.submitting = false;

            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script>

<style scoped>

    .showDropdown
    {
        display: block !important;
    }

    .input-group-append
    {
      height: fit-content;
    }

    .url-preview-link
    {
        font-size: 0.85rem;
        word-break: break-all;
    }

    .menu-preview
    {
        padding: 12px;
        background-color: #f8f9fa;
        border-radius: 4px;
        border: 1px solid #e3e8ed;
    }

    .menu-preview-list
    {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .menu-preview-list li
    {
        display: flex;
        align-items: center;
        padding: 6px 8px;
        border-bottom: 1px solid #e3e8ed;
        font-size: 0.9rem;
    }

    .menu-preview-list li:last-child
    {
        border-bottom: none;
    }

    .menu-preview-position
    {
        display: inline-block;
        min-width: 28px;
        font-weight: 600;
        color: #6c757d;
        margin-right: 8px;
    }

    .menu-preview-label
    {
        flex-grow: 1;
    }

    .menu-preview-current
    {
        background-color: #e8f0fe;
        border-radius: 4px;
    }

</style>